import { DownloadIcon } from '@components/Icons/Download';
import { askAgentAboutMoments } from '@lib/starzero-agents';
import { showToast } from '@lib/utils/toastmaster';
import { pluralize } from '@lib/utils/units';
import { verbMenuIcon, VERBS } from '@lib/verbs';
import CloseIcon from '@mui/icons-material/Close';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import {
  decodeMomentFromString,
  MomentIdentifierString,
} from '@stores/MomentSelection/MomentSelection';
import { trackingStore } from '@stores/Tracking';
import { useUIPreferences } from '@stores/UIPreferences';
import { observer } from 'mobx-react';
import { BaseSelectionBar } from './BaseSelectionBar';
import { MomentMergeButton } from './MomentMergeButton';

export const MomentSelectionBar = observer(function MomentSelectionBar() {
  const libraryManager = useLibraryManager();
  const uiPreferences = useUIPreferences();
  const momentSelection = libraryManager.multiLibSearch.momentSelection;
  const momentCount = momentSelection.selectedMoments.size;

  const downloadDisabled = (() => {
    if (momentCount === 0) {
      return {
        value: true,
        reason: 'Please select at least one moment to download.',
      };
    }
    if (libraryManager.renderer.isRendering || libraryManager.renderer.isDownloading) {
      return {
        value: true,
        reason:
          'A download is already in progress. Please wait for it to finish before downloading again.',
      };
    }
    return {
      value: false,
      reason: 'Download watermarked version of the selected moments for free.',
    };
  })();

  const downloadSelection = async () => {
    if (!libraryManager.multiLibSearch.results) {
      return;
    }
    trackingStore.selectionBarDownload({ momentCount });

    const searchText = libraryManager.multiLibSearch.request?.text ?? 'result';

    showToast('moment-downloading', { count: momentCount });
    libraryManager.renderer.setIsDownloading(true);

    try {
      const promises = Array.from(momentSelection.selectedMoments.values())
        .map((momentId, index) => {
          const moment = decodeMomentFromString(momentId as MomentIdentifierString);
          const clip = {
            libraryId: moment.libraryId,
            mediaId: moment.mediaId,
            startTime: moment.startTime,
            endTime: moment.endTime,
          };
          return libraryManager.renderer.createCompilation([clip], `${searchText}_${index}.mp4`);
        })
        .filter((promise): promise is Promise<void> => promise !== null);

      const results = await Promise.allSettled(promises);

      const failedCount = results.filter((result) => result.status === 'rejected').length;
      const successCount = results.length - failedCount;

      // Show individual error messages for each failure
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const error = result.reason;
          showToast('moment-download-failed-individual', {
            index: index + 1,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        }
      });

      if (successCount > 0) {
        showToast('moment-download-complete', { count: successCount });
        libraryManager.renderer.setIsDownloading(false);
      }
    } catch (error) {
      libraryManager.renderer.setIsDownloading(false);
      showToast('moment-download-failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  const canOpenSelectionInChat = (() => {
    const libraryIds = new Set(
      Array.from(momentSelection.selectedMoments.values()).map((momentId) => {
        // decode then take libraryId
        const moment = decodeMomentFromString(momentId as MomentIdentifierString);
        return moment.libraryId;
      }),
    );
    return libraryIds.size === 1 ? [...libraryIds][0] : null;
  })();

  const totalAvailable = libraryManager.multiLibSearch.results?.results.length ?? 0;
  const allSelected = totalAvailable > 0 && momentCount >= totalAvailable;

  return (
    <BaseSelectionBar
      variant="moment-search"
      allSelected={allSelected}
      selectedCountProps={{
        text: () => `${pluralize(momentCount, 'moment')} selected`,
      }}
      selectAllProps={{
        text: 'Select all',
        onClick: () => {
          trackingStore.selectionBarSelectAll({
            totalAvailable: libraryManager.multiLibSearch.results?.results.length ?? 0,
          });
          momentSelection.selectAll();
        },
      }}
      deselectAllProps={{
        text: 'Deselect all',
        onClick: () => {
          trackingStore.selectionBarDeselectAll();
          momentSelection.deselectAll();
        },
      }}
      closeProps={{
        text: 'Close',
        onClick: () => {
          trackingStore.selectionBarClosed();
          momentSelection.stopSelecting();
          // Focus back on search bar
          if (uiPreferences.searchBarFocusCallback) {
            uiPreferences.searchBarFocusCallback();
          }
        },
        tooltip: 'Close the moment selection',
        children: <CloseIcon />,
      }}
      downloadProps={{
        text: 'Download',
        onClick: downloadSelection,
        disabled: downloadDisabled.value,
        children: <DownloadIcon disabled={downloadDisabled.value} useCurrentColor />,
        tooltip: downloadDisabled.reason,
      }}
      askAnAgentProps={{
        // Bulk version of the same verb: same label, same glyph. Only the event differs
        // (`selectionBarAskAnAgent`), because this one reports a count rather than one moment.
        text: VERBS['work-with-agent'].label,
        disabled: !canOpenSelectionInChat,
        onClick: () => {
          const libraryId = canOpenSelectionInChat;
          if (libraryId) {
            trackingStore.selectionBarAskAnAgent({ momentCount, libraryId });
            askAgentAboutMoments(
              libraryId,
              Array.from(libraryManager.multiLibSearch.momentSelection.selectedItems)
                .map((momentId) => {
                  const searchContext =
                    libraryManager.multiLibSearch.momentSelection.searchContextOnSelect.get(
                      momentId as MomentIdentifierString,
                    );
                  const decodedMoment = decodeMomentFromString(momentId as MomentIdentifierString);
                  if (!searchContext) {
                    console.log(momentId, searchContext);
                    return null;
                  }
                  return {
                    libraryId: decodedMoment.libraryId,
                    searchType: searchContext.resultType,
                    mediaId: decodedMoment.mediaId,
                    beginTimestamp: decodedMoment.startTime,
                    endTimestamp: decodedMoment.endTime || 1000,
                  };
                })
                .filter((moment) => moment !== null),
              {
                surface: 'search',
                control: 'moment-selection-bar',
              },
            );
          }
        },
        tooltip: canOpenSelectionInChat
          ? 'Ask an agent about the selected moments'
          : 'Asking an agent about moments from different libraries is not yet supported',
        children: verbMenuIcon('work-with-agent'),
      }}
    >
      <MomentMergeButton libraryManager={libraryManager} />
    </BaseSelectionBar>
  );
});
